import { Locale, RedisTtlConfig, Scope } from '@headless-workspace/config';
import {
    CachedRepository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { CachingHelpers, DecoderHelpers } from '@headless-workspace/utils';
import { FooterSlotDTO, FooterSlotsContainerDTO } from './FooterSlotsDTO';
import { FooterSlotsValue, mapFooterSlots } from './FooterSlotsValue';

const CACHE_SERVICE_FOOTER_SLOTS_PREFIX = 'slots';
const CACHE_SERVICE_FOOTER_SLOTS_KEY = 'footer';

export interface FooterSlotsRepositorySpec {
    getFooterInformation(locale: Locale): Promise<Result<FooterSlotsValue>>;
}

export class FooterSlotsRepository extends CachedRepository implements FooterSlotsRepositorySpec {
    path = '/content/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    private async getRemoteFooterSlots(locale: Locale): Promise<Result<FooterSlotsValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/slots`, {
                locale,
                scope: Scope.Footer,
            });

            const slotsContainer = FooterSlotsContainerDTO.parse(response);
            const slots = DecoderHelpers.safeParseArray(slotsContainer.slots, FooterSlotDTO);

            const footerSlots = mapFooterSlots({ slots });
            if (
                footerSlots.reinsurance.length === 0 &&
                footerSlots.links.length === 0 &&
                footerSlots.compliance.length === 0 &&
                footerSlots.socialNetwork.length === 0
            ) {
                return ResultEmpty();
            }

            return ResultSuccess(footerSlots);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getFooterInformation(locale: Locale): Promise<Result<FooterSlotsValue>> {
        const params = { locale };

        return this.withCache(
            locale,
            CACHE_SERVICE_FOOTER_SLOTS_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_FOOTER_SLOTS_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(
                    CACHE_SERVICE_FOOTER_SLOTS_PREFIX,
                    CACHE_SERVICE_FOOTER_SLOTS_KEY,
                    params,
                ),
            },
            () => this.getRemoteFooterSlots(locale),
        );
    }
}
